import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './Icon';
import Modal from './Modal';
import { getAccessToken, isSuperAdminContextUser } from '../authStorage';
import {
  createSuperAdminCeo,
  fetchSuperAdminCeos,
  fetchSuperAdminCompanies,
  updateSuperAdminCeo,
} from '../superAdminCeosApi';
import { useLanguage } from '../i18n/language';
import './BranchesPage.css';
import './DecisionsPage.css';
import './Modal.css';
import './UsersPage.css';

const emptyForm = () => ({
  companyId: '',
  userName: '',
  fullName: '',
  email: '',
  phone: '',
  password: '',
  isActive: true,
});

const PAGE_SIZE = 10;

export default function SuperAdminCeosPage() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = {
    loadFailed: isAr ? 'تعذر تحميل البيانات' : 'Unable to load data',
    addFailed: isAr ? 'تعذر إضافة الرئيس التنفيذي' : 'Unable to add CEO',
    updateFailed: isAr ? 'تعذر تحديث الرئيس التنفيذي' : 'Unable to update CEO',
    required: isAr ? 'الشركة واسم المستخدم وكلمة المرور مطلوبة' : 'Company, username and password are required',
    pageTitle: isAr ? 'إدارة الرؤساء التنفيذيين' : 'CEOs Management',
    pageSub: isAr ? 'إضافة وتعديل حسابات الرؤساء التنفيذيين على مستوى جميع الشركات.' : 'Create and manage CEO accounts across all companies.',
    add: isAr ? 'إضافة رئيس تنفيذي' : 'Add CEO',
    edit: isAr ? 'تعديل' : 'Edit',
    save: isAr ? 'حفظ' : 'Save',
    saveChanges: isAr ? 'حفظ التغييرات' : 'Save changes',
    adding: isAr ? 'جارٍ الإضافة...' : 'Adding...',
    saving: isAr ? 'جارٍ الحفظ...' : 'Saving...',
    cancel: isAr ? 'إلغاء' : 'Cancel',
    modalAddTitle: isAr ? 'إضافة رئيس تنفيذي' : 'Add CEO',
    modalEditTitle: isAr ? 'تعديل رئيس تنفيذي' : 'Edit CEO',
    total: isAr ? 'الإجمالي' : 'Total',
    active: isAr ? 'نشط' : 'Active',
    inactive: isAr ? 'غير نشط' : 'Inactive',
    search: isAr ? 'بحث بالاسم أو اسم المستخدم أو الشركة...' : 'Search by name, username, or company...',
    loading: isAr ? 'جارٍ تحميل الرؤساء التنفيذيين...' : 'Loading CEOs...',
    noResults: isAr ? 'لا يوجد نتائج.' : 'No results.',
    showing: isAr ? 'عرض' : 'Showing',
    from: isAr ? 'من' : 'of',
    company: isAr ? 'الشركة' : 'Company',
    username: isAr ? 'اسم المستخدم' : 'Username',
    fullName: isAr ? 'الاسم الكامل (اختياري)' : 'Full name (optional)',
    email: isAr ? 'البريد الإلكتروني (اختياري)' : 'Email (optional)',
    phone: isAr ? 'رقم الجوال (اختياري)' : 'Phone (optional)',
    password: isAr ? 'كلمة المرور' : 'Password',
    newPasswordHint: isAr ? 'كلمة مرور جديدة (اختياري)' : 'New password (optional)',
    activeAccount: isAr ? 'الحساب نشط (يمكن تسجيل الدخول)' : 'Account active (can sign in)',
    activeLabel: isAr ? 'نشط' : 'Active',
    inactiveLabel: isAr ? 'غير نشط' : 'Inactive',
    colFullName: isAr ? 'الاسم الكامل' : 'Full name',
    colUsername: isAr ? 'اسم المستخدم' : 'Username',
    colCompany: isAr ? 'الشركة' : 'Company',
    colStatus: isAr ? 'الحالة' : 'Status',
    deniedSub: isAr ? 'هذه الصفحة متاحة للسوبر أدمن فقط' : 'This page is available for Super Admin only',
  };

  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [ceos, setCeos] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRows, setTotalRows] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [modalOpen, setModalOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const loadData = useCallback(async ({ page = 1, search = query } = {}) => {
    setLoading(true);
    setError('');
    try {
      const [ceoRes, companyRows] = await Promise.all([
        fetchSuperAdminCeos({ page, pageSize: PAGE_SIZE, q: search }),
        fetchSuperAdminCompanies(),
      ]);
      setCeos(ceoRes.items);
      setCompanies(companyRows);
      setCurrentPage(ceoRes.page);
      setTotalRows(ceoRes.total);
      setTotalPages(ceoRes.totalPages);
    } catch (e) {
      setError(e instanceof Error ? e.message : t.loadFailed);
    } finally {
      setLoading(false);
    }
  }, [t.loadFailed, query]);

  useEffect(() => {
    if (!getAccessToken()) {
      navigate('/login', { replace: true });
      return;
    }
    if (!isSuperAdminContextUser()) {
      setAccessDenied(true);
      setLoading(false);
      return;
    }
    loadData({ page: 1, search: '' });
  }, [navigate, loadData]);

  const toggleActive = async (u) => {
    if (togglingId) return;
    setTogglingId(u.id);
    try {
      await updateSuperAdminCeo(u.id, {
        companyId: Number(u.companyId),
        userName: u.userName,
        fullName: u.fullName || undefined,
        email: u.email || undefined,
        phone: u.phone || undefined,
        isActive: !u.isActive,
      });
      setCeos((prev) => prev.map((c) => c.id === u.id ? { ...c, isActive: !u.isActive } : c));
    } catch (e) {
      setError(e instanceof Error ? e.message : t.updateFailed);
    } finally {
      setTogglingId(null);
    }
  };

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setError('');
    setModalOpen(true);
  };

  const openEdit = (u) => {
    setEditId(u.id);
    setForm({
      companyId: String(u.companyId),
      userName: u.userName,
      fullName: u.fullName || '',
      email: u.email || '',
      phone: u.phone || '',
      password: '',
      isActive: !!u.isActive,
    });
    setError('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditId(null);
    setForm(emptyForm());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.companyId || !form.userName.trim() || (!editId && !form.password.trim())) {
      setError(t.required);
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      if (editId) {
        await updateSuperAdminCeo(editId, {
          companyId: Number(form.companyId),
          userName: form.userName.trim(),
          fullName: form.fullName.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
          newPassword: form.password.trim() || undefined,
          isActive: form.isActive,
        });
      } else {
        await createSuperAdminCeo({
          companyId: Number(form.companyId),
          userName: form.userName.trim(),
          password: form.password,
          fullName: form.fullName.trim() || undefined,
          email: form.email.trim() || undefined,
          phone: form.phone.trim() || undefined,
        });
      }
      closeModal();
      await loadData({ page: currentPage, search: query });
    } catch (e) {
      setError(e instanceof Error ? e.message : (editId ? t.updateFailed : t.addFailed));
    } finally {
      setSubmitting(false);
    }
  };

  const handleSearchChange = (e) => {
    const next = e.target.value;
    setQuery(next);
    loadData({ page: 1, search: next });
  };

  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    loadData({ page, search: query });
  };

  if (accessDenied) {
    return (
      <div className="page">
        <div className="page__topbar">
          <div className="page__topbar-left">
            <span className="page__topbar-title">{t.pageTitle}</span>
            <span className="page__topbar-sub">{t.deniedSub}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page users-page">
      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editId ? t.modalEditTitle : t.modalAddTitle}
        subtitle={t.pageSub}
        icon={<Icon name="user" size={18} color="#fff" />}
        size="lg"
        variant="drawer-left"
        headerActions={(
          <button type="submit" form="ceo-modal-form" className="btn btn--primary" disabled={submitting}>
            {submitting ? (editId ? t.saving : t.adding) : editId ? t.saveChanges : t.save}
          </button>
        )}
        footer={(
          <>
            <button type="button" className="btn btn--ghost" disabled={submitting} onClick={closeModal}>{t.cancel}</button>
          </>
        )}
      >
        <form id="ceo-modal-form" className="mform" onSubmit={handleSubmit}>
          {error && (
            <div className="mform__field mform__field--full" style={{ marginTop: -6 }}>
              <div className="mform__error" style={{ padding: '10px 12px', background: '#ffebee', borderRadius: 10 }}>{error}</div>
            </div>
          )}
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.company} <span>*</span></label>
              <select className="mform__select" value={form.companyId} onChange={(e) => setForm((p) => ({ ...p, companyId: e.target.value }))}>
                <option value="">{isAr ? 'اختر الشركة' : 'Select company'}</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.username} <span>*</span></label>
              <input className="mform__input" value={form.userName} onChange={(e) => setForm((p) => ({ ...p, userName: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.fullName}</label>
              <input className="mform__input" value={form.fullName} onChange={(e) => setForm((p) => ({ ...p, fullName: e.target.value }))} />
            </div>
            <div className="mform__field">
              <label className="mform__label">{t.email}</label>
              <input className="mform__input" type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
            </div>
          </div>
          <div className="mform__row">
            <div className="mform__field">
              <label className="mform__label">{t.phone}</label>
              <input className="mform__input" value={form.phone} onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
            </div>
            <div className="mform__field">
              <label className="mform__label">{editId ? t.newPasswordHint : t.password}{!editId && <span>*</span>}</label>
              <input className="mform__input" type="password" value={form.password} onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))} />
            </div>
          </div>
          {editId && (
            <div className="mform__field mform__field--full">
              <label className="mform__label" style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <input type="checkbox" checked={form.isActive} onChange={(e) => setForm((p) => ({ ...p, isActive: e.target.checked }))} />
                {t.activeAccount}
              </label>
            </div>
          )}
        </form>
      </Modal>

      <div className="list-page-card list-page-card--unified">
        <header className="list-page-card__header">
          <div className="list-page-card__titles">
            <span className="list-page-card__title">
              <Icon name="user" size={18} color="#00334a" style={{ verticalAlign: 'middle', marginLeft: 6 }} />
              {t.pageTitle}
            </span>
            <span className="list-page-card__sub">{t.pageSub}</span>
          </div>
          <div className="list-page-card__actions">
            <button type="button" className="btn btn--primary" onClick={openCreate}>
              <Icon name="plus" size={14} color="#fff" />
              {t.add}
            </button>
          </div>
        </header>

        <div className="page__stats">
        <div className="page__stat-chip page__stat-chip--total"><Icon name="user" size={13} /> {t.total}: {totalRows}</div>
        <div className="page__stat-chip page__stat-chip--active"><Icon name="check-circle" size={13} /> {t.active}: {ceos.filter((u) => u.isActive).length}</div>
        <div className="page__stat-chip page__stat-chip--new"><Icon name="x-circle" size={13} /> {t.inactive}: {ceos.filter((u) => !u.isActive).length}</div>
        </div>

        <div className="page__filters">
        <div className="page__search">
          <Icon name="search" size={14} color="#90a4ae" />
          <input className="page__search-input" value={query} onChange={handleSearchChange} placeholder={t.search} />
        </div>
        </div>

        {error && !modalOpen && <div className="list-page-card__banner list-page-card__banner--error">{error}</div>}
        {loading && <div className="list-page-card__loading">{t.loading}</div>}

        {!loading && (
        <div className="table-card">
          <table className="data-table">
            <thead>
              <tr>
                <th style={{ width: 42 }}>#</th>
                <th>{t.colFullName}</th>
                <th>{t.colUsername}</th>
                <th>{t.colCompany}</th>
                <th>{t.colStatus}</th>
              </tr>
            </thead>
            <tbody>
              {ceos.map((u, idx) => (
                <tr key={u.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(u)}>
                  <td className="td-num">{(currentPage - 1) * PAGE_SIZE + idx + 1}</td>
                  <td><strong>{u.fullName || '—'}</strong></td>
                  <td dir="ltr" style={{ textAlign: 'inherit' }}>{u.userName || '—'}</td>
                  <td>{u.companyName || '—'}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <button
                        type="button"
                        className={`sa-toggle${u.isActive ? ' sa-toggle--on' : ''}`}
                        disabled={togglingId === u.id}
                        onClick={(e) => { e.stopPropagation(); toggleActive(u); }}
                        title={u.isActive ? t.activeLabel : t.inactiveLabel}
                      >
                        <span className="sa-toggle__thumb" />
                      </button>
                      <span style={{ fontSize: '0.78rem', fontWeight: 700, color: u.isActive ? '#02c3a6' : '#90a4ae' }}>
                        {u.isActive ? t.activeLabel : t.inactiveLabel}
                      </span>
                      <button
                        type="button"
                        className="btn btn--ghost"
                        style={{ padding: '4px 10px', fontSize: '0.76rem' }}
                        onClick={(e) => { e.stopPropagation(); openEdit(u); }}
                      >
                        <Icon name="edit" size={12} /> {t.edit}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {ceos.length === 0 && <div className="users-empty">{t.noResults}</div>}
          {totalPages > 1 && (
            <div className="pagination">
              <span>
                {t.showing} {Math.min(totalRows, (currentPage - 1) * PAGE_SIZE + 1)}-
                {Math.min(totalRows, currentPage * PAGE_SIZE)} {t.from} {totalRows}
              </span>
              <div className="pagination__pages">
                <button type="button" className="pagination__btn" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>‹</button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    type="button"
                    className={`pagination__btn${currentPage === p ? ' pagination__btn--active' : ''}`}
                    onClick={() => goToPage(p)}
                  >
                    {p}
                  </button>
                ))}
                <button type="button" className="pagination__btn" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </div>
  );
}
